
import React from 'react';
import { SalProvider, useSal } from './contexts/SalContext';
import { AppMode } from './types';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import * as Sentry from '@sentry/react';

// Components
import { Header } from './components/Header';
import { ControlsPanel } from './components/ControlsPanel';
import { RouteMap } from './components/RouteMap';
import { TriageSidebar } from './components/layout/TriageSidebar';
import { OperationalMonitor } from './components/layout/OperationalMonitor';
import { OrderListWrapper as OrderList } from './components/OrderListWrapper';
import { LockScreen } from './components/LockScreen';
import { DriverDashboardView, DriverLoginView } from './components/DriverApp';
import { MagicPasteListener } from './components/MagicPasteListener';
import { PasswordResetScreen } from './components/PasswordResetScreen';
import { GlobalModals } from './components/GlobalModals';
import { AnalyticsDashboard } from './components/AnalyticsDashboard';

function MainApp() {
  const {
    isAuthenticated, isAuthLoading, isDataReady,
    userProfile, appMode, setAppMode,
    currentDriverId, setCurrentDriverId,
    orders, drivers,
    safeConfig, context, intelligence,
    actions, storeId, handleLogout, handleLocationUpdate,
    isRecoveryMode, setIsRecoveryMode, viewMode, isAdmin,
    focusedOrderId, setFocusedOrderId, setMapDisplayMode,
    setActiveModalOrder, isSimpleView, cardViewMode, setCardViewMode,
    driverTheme, toggleDriverTheme
  } = useSal();

  const { smartSplit, historicalOrders, pendingOrders, assignedOrders, thirdPartyOrders, otherOrders } = intelligence;

  const resolvedDriverId = userProfile?.role === 'driver'
    ? (userProfile.associatedDriverId || currentDriverId)
    : currentDriverId;
  const driverActiveOrders = React.useMemo(() => {
    if (!resolvedDriverId) return [];
    return orders.filter(o => o.driverId === resolvedDriverId && (o.status === 'assigned' || o.status === 'dispatched'));
  }, [orders, resolvedDriverId]);

  if (isAuthLoading) return (
    <div className="h-screen w-screen bg-app-bg flex flex-col items-center justify-center gap-4">
      <div className="p-4 bg-sal-cyber-teal/10 rounded-3xl border border-sal-cyber-teal/20"><Loader2 className="w-8 h-8 text-sal-cyber-teal animate-spin" /></div>
      <p className="text-text-muted font-black text-xs uppercase tracking-[0.4em]">Proteção Supabase v3.4</p>
    </div>
  );

  if (isRecoveryMode) return <PasswordResetScreen onDone={() => setIsRecoveryMode(false)} />;

  if (!isAuthenticated) return <LockScreen onUnlock={() => { }} />;

  if (!isDataReady) return (
    <div className="h-screen w-screen bg-app-bg flex flex-col items-center justify-center gap-4">
      <Loader2 className="w-12 h-12 text-sal-cyber-teal animate-spin" />
      <div className="text-center">
        <p className="text-sal-cyber-teal font-black text-sm uppercase tracking-[0.3em] animate-pulse">Sincronizando Cérebro Logístico…</p>
        <p className="text-text-muted text-xs font-bold uppercase tracking-widest mt-2">Permissões: {userProfile?.role?.toUpperCase()}</p>
      </div>
    </div>
  );

  if (userProfile?.role === 'driver' || appMode === AppMode.DRIVER_DASHBOARD || appMode === AppMode.DRIVER_LOGIN) {
    if (appMode === AppMode.DRIVER_LOGIN) {
      return (
        <DriverLoginView
          drivers={drivers}
          onLogin={(id) => {
            setCurrentDriverId(id);
            setAppMode(AppMode.DRIVER_DASHBOARD);
            sessionStorage.setItem('sal_current_driver_id', id);
          }}
          onBackToDispatcher={() => setAppMode(AppMode.DISPATCHER)}
        />
      );
    }

    const drv = drivers.find(d => d.id === resolvedDriverId);
    if (userProfile?.role === 'driver' && !drv) return (
      <div className="h-screen w-screen bg-app-bg flex flex-col items-center justify-center p-8 text-center">
        <ShieldAlert className="w-16 h-16 text-rose-500 mb-6" />
        <h2 className="text-text-primary text-3xl font-black uppercase mb-2">Erro de Perfil</h2>
        <p className="text-text-secondary text-base mb-8">Seu usuário não está vinculado a um motorista ativo.</p>
        <button type="button" onClick={handleLogout} className="text-sal-cyber-teal font-black uppercase text-sm">Sair do Sistema</button>
      </div>
    );
    if (drv) return (
        <DriverDashboardView
          driver={drv}
          orders={driverActiveOrders}
          config={safeConfig}
          context={context}
          smartSplit={smartSplit}
          onConfirmPickup={() => actions.handleRealPickup(drv.id)}
          onCompleteOrder={(id, options) => actions.handleRealDelivery(id, options)}
          onArrived={(id) => actions.handleArrived(id)}
          onLogout={handleLogout}
          onLocationUpdate={handleLocationUpdate}
          driverTheme={driverTheme}
          onToggleDriverTheme={toggleDriverTheme}
          isAdmin={userProfile?.role === 'admin'}
          onBackToDispatcher={() => setAppMode(AppMode.DISPATCHER)}
          storeId={storeId || ''}
          // v60.3: Passando historicalOrders para cálculo correto de entregas do dia
          historicalOrders={historicalOrders}
      />
    );
  }

  return (
    <div className="app h-screen w-screen bg-app-bg text-text-primary flex flex-col overflow-hidden font-sans selection:bg-sal-cyber-teal/30">
      <Toaster position="top-center" containerStyle={{ zIndex: 100000 }} aria-live="polite" />
      <MagicPasteListener onOrderParsed={actions.handleOrderParsed} systemConfig={safeConfig} />
      
      <Header />
      
      <div className="main-content flex-1 flex flex-col overflow-hidden">
        <ControlsPanel />
        
        <div className="content flex-1 flex overflow-hidden relative">
          {viewMode === 'analytics' ? (
            <div className="w-full h-full animate-in fade-in slide-in-from-bottom-4 duration-500">
              <Sentry.ErrorBoundary fallback={<div className="h-full w-full flex items-center justify-center text-rose-400 text-sm font-bold uppercase tracking-wider">Falha ao carregar Analytics</div>}>
                <React.Suspense fallback={<div className="h-full w-full flex items-center justify-center text-slate-400 text-sm font-bold uppercase tracking-wider">Carregando Analytics...</div>}>
                  <AnalyticsDashboard historicalOrders={historicalOrders} drivers={drivers} config={safeConfig} storeId={storeId || ''} isAdmin={isAdmin} />
                </React.Suspense>
              </Sentry.ErrorBoundary>
            </div>
          ) : (
            <>
              {/* Coluna 1: Triagem (esquerda) */}
              <TriageSidebar 
                pendingOrders={pendingOrders} 
                focusedOrderId={focusedOrderId} 
                onToggleFocus={(id) => { setFocusedOrderId(id); if (!id) setMapDisplayMode('tactical'); }} 
                onDeleteOrder={actions.onOrderDelete} 
                onEditOrder={(order) => { setActiveModalOrder(order); actions.toggleModal('edit', true); }} 
                drivers={drivers} 
                onAssignDriver={actions.handleAssignDriver} 
                config={safeConfig} 
                isSimpleView={isSimpleView} 
                onDispatchCluster={actions.handleDispatchCluster} 
                onToggleReady={actions.handleToggleReady} 
                onNotifyDelay={(id) => {}} 
                onClearQueue={actions.handleClearQueue} 
                handleDeleteMultipleOrders={actions.handleDeleteMultipleOrders} 
                viewMode={viewMode} 
                cardViewMode={cardViewMode} 
                onToggleCardView={setCardViewMode} 
                onConfirmOrder={actions.handleConfirmOrder} 
                onTrackOrder={actions.handleTrackOrder} 
                onManualComplete={(id) => actions.handleRealDelivery(id)} 
                suggestion={smartSplit} 
              />
              
              {/* Coluna 2: Cérebro Logístico (centro) */}
              <RouteMap />
              
              {/* Coluna 3: Monitoramento (direita) */}
              <OperationalMonitor 
                assignedOrders={assignedOrders} 
                thirdPartyOrders={thirdPartyOrders} 
                otherOrders={otherOrders} 
                drivers={drivers} 
                config={safeConfig} 
                onRecallOrder={actions.handleRecallOrder} 
                onReportIncident={(order) => { setActiveModalOrder(order); actions.toggleModal('incident', true); }} 
                onManualComplete={(id) => actions.handleRealDelivery(id)} 
              />
            </>
          )}
        </div>
      </div>

      <GlobalModals />

      <div className="absolute bottom-1 right-12 text-xs text-slate-500/10 font-medium z-10 pointer-events-none select-none">
        reCAPTCHA: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="pointer-events-auto">Privacy</a> / <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="pointer-events-auto">Terms</a>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <SalProvider>
      <MainApp />
    </SalProvider>
  );
}
